import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  XCircle,
  RefreshCw,
  Home,
  Phone,
  Mail,
  Shield,
  Ticket,
  Calendar,
  AlertTriangle,
  ChevronRight,
  ArrowLeft,
  Wifi,
  WifiOff,
  CreditCard,
  Smartphone,
  MessageCircle,
  HelpCircle,
} from "lucide-react";
import Footer from "../../components/landing/Footer";
import GuestNavbar from "../../components/navigations/GuestNavbar";
import AuthNavbar from "../../components/navigations/AuthNavbar";
import { useAppSelector } from "../../store/hooks";

// ─────────────────────────────────────────────────────────────────
// Per-type configuration
// ─────────────────────────────────────────────────────────────────
const TYPE_CONFIG = {
  insurance: {
    gradient: "from-emerald-600 to-green-600",
    lightBg: "from-emerald-50 to-green-50",
    border: "border-emerald-200",
    iconColor: "text-emerald-600",
    retryPath: "/checkout",
    retryLabel: "Retry Payment",
    browseLabel: "Browse Insurance",
    browsePath: "/insurance",
    typeLabel: "Insurance Payment",
    icon: Shield,
  },
  event: {
    gradient: "from-emerald-600 to-teal-600",
    lightBg: "from-teal-50 to-emerald-50",
    border: "border-teal-200",
    iconColor: "text-teal-600",
    retryPath: null, // constructed from slug
    retryLabel: "Retry Payment",
    browseLabel: "Browse Events",
    browsePath: "/events",
    typeLabel: "Event Payment",
    icon: Ticket,
  },
  conference: {
    gradient: "from-orange-500 to-orange-600",
    lightBg: "from-orange-50 to-amber-50",
    border: "border-orange-200",
    iconColor: "text-orange-600",
    retryPath: null, // constructed from slug
    retryLabel: "Retry Payment",
    browseLabel: "Browse Conferences",
    browsePath: "/conference-trade-mission",
    typeLabel: "Conference Payment",
    icon: Calendar,
  },
  quote: {
    gradient: "from-emerald-600 to-green-600",
    lightBg: "from-emerald-50 to-green-50",
    border: "border-emerald-200",
    iconColor: "text-emerald-600",
    retryPath: "/my-quote-requests",
    retryLabel: "View My Quotes",
    browseLabel: "Browse Insurance",
    browsePath: "/insurance",
    typeLabel: "Quote Payment",
    icon: Shield,
  },
};

// Maps common error codes / messages to user-friendly explanations
const ERROR_EXPLANATIONS = {
  insufficient_funds: {
    title: "Insufficient Funds",
    message: "Your Mobile Money wallet doesn't have enough balance for this transaction.",
    fix: "Please top up your wallet and try again.",
  },
  invalid_number: {
    title: "Invalid Phone Number",
    message: "The mobile money number entered is invalid or not registered.",
    fix: "Double-check the number and ensure it's active on the network.",
  },
  network_error: {
    title: "Network Error",
    message: "We couldn't reach your mobile money network at this time.",
    fix: "Wait a few minutes and try again, or use a different network.",
  },
  timeout: {
    title: "Request Timed Out",
    message: "The payment prompt wasn't responded to in time.",
    fix: "Check your phone for the payment prompt and try again.",
  },
  declined: {
    title: "Payment Declined",
    message: "Your payment was declined by the payment provider.",
    fix: "Contact your mobile money provider or try a different payment method.",
  },
  cancelled: {
    title: "Payment Cancelled",
    message: "The payment was cancelled before it could be completed.",
    fix: "Start a new payment when you're ready to proceed.",
  },
  duplicate: {
    title: "Duplicate Transaction",
    message: "A similar transaction is already being processed.",
    fix: "Wait a few minutes before attempting the payment again.",
  },
};

// Common troubleshooting steps by payment type
const TROUBLE_TIPS = {
  insurance: [
    {
      icon: Smartphone,
      color: "bg-emerald-100 text-emerald-600",
      title: "Check Your Phone",
      desc: "Ensure your phone is on and connected to the network. Look for the MoMo prompt.",
    },
    {
      icon: CreditCard,
      color: "bg-blue-100 text-blue-600",
      title: "Check Wallet Balance",
      desc: "Make sure your Mobile Money wallet has sufficient funds for this transaction.",
    },
    {
      icon: RefreshCw,
      color: "bg-purple-100 text-purple-600",
      title: "Try Again",
      desc: "Most payment issues are temporary. Try again and the payment should succeed.",
    },
    {
      icon: MessageCircle,
      color: "bg-amber-100 text-amber-600",
      title: "Contact Support",
      desc: "If the problem persists, reach out to our team. We'll resolve it quickly.",
    },
  ],
  event: [
    {
      icon: Smartphone,
      color: "bg-teal-100 text-teal-600",
      title: "Check Your Phone",
      desc: "Look for the Mobile Money prompt on your device and approve it.",
    },
    {
      icon: CreditCard,
      color: "bg-blue-100 text-blue-600",
      title: "Wallet Balance",
      desc: "Top up your Mobile Money wallet if the balance is low.",
    },
    {
      icon: Ticket,
      color: "bg-emerald-100 text-emerald-600",
      title: "Event Still Available",
      desc: "Your selected tickets are still available. Complete payment to secure them.",
    },
    {
      icon: MessageCircle,
      color: "bg-amber-100 text-amber-600",
      title: "Need Help?",
      desc: "Our team is available to assist you through the booking process.",
    },
  ],
  conference: [
    {
      icon: Smartphone,
      color: "bg-orange-100 text-orange-600",
      title: "Approve the Prompt",
      desc: "Check your phone for the Mobile Money payment prompt.",
    },
    {
      icon: CreditCard,
      color: "bg-blue-100 text-blue-600",
      title: "Check Balance",
      desc: "Ensure your wallet has enough funds to cover the registration fee.",
    },
    {
      icon: Calendar,
      color: "bg-emerald-100 text-emerald-600",
      title: "Spot Still Reserved",
      desc: "Your registration spot is held for a short period. Complete payment soon.",
    },
    {
      icon: MessageCircle,
      color: "bg-amber-100 text-amber-600",
      title: "Contact Support",
      desc: "Reach out if you're experiencing persistent issues.",
    },
  ],
};

// ─────────────────────────────────────────────────────────────────
// Animated error icon
// ─────────────────────────────────────────────────────────────────
function ErrorIcon() {
  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", stiffness: 150, damping: 12 }}
      className="relative inline-flex items-center justify-center mb-6"
    >
      <div className="relative flex items-center justify-center w-32 h-32 bg-gradient-to-br from-red-500 to-rose-600 rounded-full shadow-2xl">
        <XCircle size={60} className="text-white drop-shadow-lg" />
      </div>

      {/* Pulsing ring */}
      <motion.div
        className="absolute w-32 h-32 rounded-full border-2 border-red-400"
        animate={{ scale: [1, 1.5], opacity: [0.6, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeOut" }}
      />
      <motion.div
        className="absolute w-32 h-32 rounded-full border-2 border-red-300"
        animate={{ scale: [1, 1.9], opacity: [0.4, 0] }}
        transition={{ duration: 1.5, delay: 0.4, repeat: Infinity, ease: "easeOut" }}
      />

      {/* Warning badges around the icon */}
      {[
        { angle: -45, delay: 0.3 },
        { angle: 45, delay: 0.5 },
        { angle: 180, delay: 0.7 },
      ].map(({ angle, delay }, i) => (
        <motion.div
          key={i}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay, type: "spring", stiffness: 200 }}
          style={{
            position: "absolute",
            transform: `rotate(${angle}deg) translateX(72px) rotate(${-angle}deg)`,
          }}
          className="w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center shadow-lg"
        >
          <AlertTriangle size={14} className="text-white" />
        </motion.div>
      ))}
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Error detail card
// ─────────────────────────────────────────────────────────────────
function ErrorCard({ payCtx, errDetails, type, cfg }) {
  const TypeIcon = cfg.icon;

  return (
    <div className="p-6 bg-red-50 border border-red-200 rounded-2xl shadow-sm space-y-4">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
          <TypeIcon size={24} className="text-red-500" />
        </div>
        <div>
          <p className="text-xs font-bold text-red-500 uppercase tracking-wider mb-1">{cfg.typeLabel}</p>
          <h3 className="text-lg font-bold text-gray-900">{errDetails?.title || "Payment Unsuccessful"}</h3>
          <p className="text-sm text-gray-600 mt-1 leading-relaxed">
            {errDetails?.message || payCtx?.reason || payCtx?.errorMessage ||
              "Your payment could not be processed. No money has been deducted from your account."}
          </p>
        </div>
      </div>

      {errDetails?.fix && (
        <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 font-medium">{errDetails.fix}</p>
        </div>
      )}

      {/* Payment details summary */}
      <div className="space-y-2 pt-3 border-t border-red-200">
        {payCtx?.reference && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Reference</span>
            <span className="font-mono text-gray-700 text-xs">{payCtx.reference}</span>
          </div>
        )}
        {payCtx?.amount && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Amount</span>
            <span className="font-medium text-gray-900">
              {type === "insurance" || type === "quote" ? "₵" : "GH₵"} {Number(payCtx.amount).toFixed(2)}
            </span>
          </div>
        )}
        {(payCtx?.policyType || payCtx?.eventTitle) && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{type === "insurance" ? "Policy" : "Event"}</span>
            <span className="font-medium text-gray-900 text-right max-w-[60%]">
              {payCtx.policyType || payCtx.eventTitle}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────
export default function PaymentFailed() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isVerified, user } = useAppSelector((s) => s.auth);

  const [payCtx, setPayCtx] = useState(null);

  // ── Parse context ──────────────────────────────────────────────
  useEffect(() => {
    const reference =
      searchParams.get("reference") || searchParams.get("trxref") || searchParams.get("ref");
    const typeParam = searchParams.get("type") || "insurance";
    const reasonParam = searchParams.get("reason");
    const statusParam = searchParams.get("status");

    let stored = null;
    try {
      const raw = sessionStorage.getItem("tripsecure_payment_context");
      if (raw) stored = JSON.parse(raw);
    } catch (_) {}

    const locState = location.state || {};

    const ctx = {
      reference: reference || stored?.reference || locState.reference,
      type: stored?.type || typeParam || locState.type || "insurance",
      subtype: stored?.subtype || locState.policyType || locState.subtype,
      policyType: stored?.policyType || locState.policyType,
      providerName: stored?.providerName || locState.providerName,
      coverLabel: stored?.coverLabel || locState.coverLabel,
      amount: stored?.amount || searchParams.get("amount") || locState.amount,
      errorMessage: stored?.errorMessage || locState.errorMessage || locState.message,
      reason: reasonParam || stored?.reason || locState.reason,
      status: statusParam || stored?.status,
      eventTitle: stored?.eventTitle || locState.eventTitle,
      slug: stored?.slug || searchParams.get("slug") || locState.slug,
      applicationId: stored?.applicationId || locState.applicationId,
      registrationId: stored?.registrationId || locState.registrationId,
      returnPath: stored?.returnPath || locState.returnPath,
    };

    setPayCtx(ctx);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const type = payCtx?.type || "insurance";
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.insurance;
  const showAuthNavbar = isAuthenticated && isVerified;

  // Decode error details
  const errorKey = payCtx?.reason?.toLowerCase().replace(/\s/g, "_");
  const errDetails = ERROR_EXPLANATIONS[errorKey] || null;

  // Resolve retry path
  const retryPath = (() => {
    if (payCtx?.returnPath) return payCtx.returnPath;
    if (cfg.retryPath) return cfg.retryPath;
    const slug = payCtx?.slug;
    if (type === "event" && slug) return `/event-details/${slug}/event-payment`;
    if (type === "conference" && slug) return `/event-details/${slug}/payment`;
    return null;
  })();

  const troubleTips = TROUBLE_TIPS[type] || TROUBLE_TIPS.insurance;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50">
      {/* Navbar */}
      {showAuthNavbar ? <AuthNavbar user={user} /> : <GuestNavbar />}

      <div className="px-4 pt-24 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-8"
            >
              {/* ─── Hero Section ─── */}
              <div className="text-center py-10">
                <ErrorIcon />

                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h1 className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-600 mb-3">
                    Payment Failed
                  </h1>
                  <p className="text-lg text-gray-500 max-w-md mx-auto">
                    Don't worry — no money was deducted from your account. You can try again at any time.
                  </p>
                </motion.div>

                {/* Status badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.45 }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 mt-6 rounded-full bg-red-100 border border-red-300 text-red-800 shadow-sm"
                >
                  <XCircle size={16} />
                  <span className="text-sm font-semibold">
                    {cfg.typeLabel} — {payCtx?.reason || payCtx?.status || "Transaction Failed"}
                  </span>
                </motion.div>
              </div>

              {/* ─── Two column layout ─── */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                {/* Left — main content */}
                <div className="space-y-6 lg:col-span-3">
                  {/* Error detail card */}
                  {payCtx && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                    >
                      <ErrorCard payCtx={payCtx} errDetails={errDetails} type={type} cfg={cfg} />
                    </motion.div>
                  )}

                  {/* Primary action buttons */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                  >
                    {retryPath && (
                      <motion.button
                        onClick={() => navigate(retryPath)}
                        whileHover={{ scale: 1.03, y: -2 }}
                        whileTap={{ scale: 0.97 }}
                        className={`flex items-center justify-center gap-2 px-5 py-4 text-sm font-bold text-white bg-gradient-to-r ${cfg.gradient} rounded-xl shadow-xl hover:shadow-2xl transition-all`}
                      >
                        <RefreshCw size={18} />
                        {cfg.retryLabel}
                      </motion.button>
                    )}

                    <motion.button
                      onClick={() => navigate(-1)}
                      whileHover={{ scale: 1.03, y: -2 }}
                      whileTap={{ scale: 0.97 }}
                      className="flex items-center justify-center gap-2 px-5 py-4 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-xl shadow-md hover:shadow-lg hover:border-gray-300 transition-all"
                    >
                      <ArrowLeft size={18} />
                      Go Back
                    </motion.button>
                  </motion.div>

                  {/* Troubleshooting tips */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                      <HelpCircle size={20} className="text-gray-400" />
                      Troubleshooting Guide
                    </h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {troubleTips.map(({ icon: Icon, color, title, desc }, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.65 + i * 0.1 }}
                          className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100"
                        >
                          <div className={`flex-shrink-0 w-9 h-9 ${color} rounded-lg flex items-center justify-center`}>
                            <Icon size={16} />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-gray-900 mb-1">{title}</p>
                            <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>

                  {/* Alternative actions */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200 shadow-sm"
                  >
                    <h3 className="text-base font-bold text-gray-900 mb-4">Other Options</h3>
                    <div className="space-y-2">
                      <button
                        onClick={() => navigate(cfg.browsePath)}
                        className="w-full flex items-center justify-between px-4 py-3.5 text-sm font-medium text-gray-700 bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all group"
                      >
                        <span className="flex items-center gap-2">
                          <cfg.icon size={16} className="text-gray-400 group-hover:text-gray-600" />
                          {cfg.browseLabel}
                        </span>
                        <ChevronRight size={16} className="text-gray-400" />
                      </button>

                      <button
                        onClick={() => navigate("/")}
                        className="w-full flex items-center justify-between px-4 py-3.5 text-sm font-medium text-gray-700 bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all group"
                      >
                        <span className="flex items-center gap-2">
                          <Home size={16} className="text-gray-400 group-hover:text-gray-600" />
                          Return to Home
                        </span>
                        <ChevronRight size={16} className="text-gray-400" />
                      </button>

                      <button
                        onClick={() => navigate("/contact-us")}
                        className="w-full flex items-center justify-between px-4 py-3.5 text-sm font-medium text-gray-700 bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all group"
                      >
                        <span className="flex items-center gap-2">
                          <MessageCircle size={16} className="text-gray-400 group-hover:text-gray-600" />
                          Contact Support
                        </span>
                        <ChevronRight size={16} className="text-gray-400" />
                      </button>
                    </div>
                  </motion.div>
                </div>

                {/* Right sidebar */}
                <div className="space-y-5 lg:col-span-2">
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.45 }}
                    className="sticky top-28 space-y-5"
                  >
                    {/* Why payments fail info */}
                    <div className="p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
                      <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
                        <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                          <AlertTriangle size={20} className="text-red-500" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900 text-sm">Common Reasons</p>
                          <p className="text-xs text-gray-400">Why payments fail</p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {[
                          {
                            icon: WifiOff,
                            color: "bg-red-100 text-red-500",
                            label: "No network connection",
                          },
                          {
                            icon: CreditCard,
                            color: "bg-amber-100 text-amber-500",
                            label: "Insufficient wallet balance",
                          },
                          {
                            icon: Smartphone,
                            color: "bg-blue-100 text-blue-500",
                            label: "Prompt not approved in time",
                          },
                          {
                            icon: XCircle,
                            color: "bg-gray-100 text-gray-400",
                            label: "Wrong PIN entered",
                          },
                        ].map(({ icon: Icon, color, label }, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div className={`w-8 h-8 ${color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                              <Icon size={14} />
                            </div>
                            <p className="text-sm text-gray-700">{label}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Support card */}
                    <div className={`p-6 bg-gradient-to-br ${cfg.lightBg} rounded-2xl border ${cfg.border} shadow-sm`}>
                      <p className="text-sm font-bold text-gray-900 mb-1">Still Having Issues?</p>
                      <p className="text-xs text-gray-600 mb-5">
                        Our support team is available to help you complete your payment.
                      </p>

                      <div className="space-y-3 mb-5">
                        <a
                          href="tel:+233302000000"
                          className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-200 hover:shadow-sm transition-all"
                        >
                          <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                            <Phone size={16} className="text-green-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">Call Us</p>
                            <p className="text-sm font-bold text-gray-900">+233 30 200 0000</p>
                          </div>
                        </a>

                        <a
                          href="mailto:support@tripsecuregh.com"
                          className="flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-200 hover:shadow-sm transition-all"
                        >
                          <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Mail size={16} className="text-blue-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-400">Email Us</p>
                            <p className="text-sm font-bold text-gray-900">support@tripsecuregh.com</p>
                          </div>
                        </a>
                      </div>

                      <button
                        onClick={() => navigate("/contact-us")}
                        className={`w-full py-3 text-sm font-bold text-white bg-gradient-to-r ${cfg.gradient} rounded-xl shadow-md hover:shadow-lg transition-all`}
                      >
                        Open Support Ticket
                      </button>
                    </div>

                    {/* Security note */}
                    <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Shield size={16} className="text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900 mb-1">Your Money Is Safe</p>
                          <p className="text-xs text-gray-500 leading-relaxed">
                            No amount was deducted from your account. Payment attempts that fail are never charged.
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <Footer />
    </div>
  );
}
